import type { TradeItem } from '@/types';
import {
  getDisplayedBookedFxRate,
  getDisplayedCobFxRate,
  getEffectiveSettlementCurrency,
  getSettlementDisplay,
  type SettlementMoneyField,
} from './settlement-ccy';
import { formatDecimalString } from '@/utils/decimal-display';
import { formatDate, formatDateTimeUtc } from '@/utils/format';
import { showsClosingCash } from './closing-cash';
import { RATE_LEG_CELLS } from './rate-legs';

/**
 * Format a trade cell value to match what is rendered in TradesTable.
 * Used by copy / copy-with-headers so the clipboard contents match the user's
 * visible table instead of raw `String(value)`. (The CSV export does NOT go
 * through this — its data cells deliberately stay native, see csv-totals C4.)
 *
 * Mirrors the cell renderers in `features/trades/components/trades-columns.tsx`.
 * LR-474/522: the money cells (Trade / Last Marked Margin Price + gross/margin
 * Values, Daily Earnings, Closing Cash) copy the displayed SETTLEMENT figure.
 * Numbers only, no currency code — spreadsheet-friendly, the Settlement Ccy
 * column carries the code.
 */
export function formatTradeCellValue(item: TradeItem, colId: string): string {
  // LR-474: the Settlement Ccy cell renders the EFFECTIVE settlement currency
  // (falls back to the security ccy), so copy must too — before the null guard,
  // which would emit '' for legacy rows without the raw field.
  if (colId === 'settlement_currency') return getEffectiveSettlementCurrency(item);

  // LR-474: settlement-primary money cells — resolved as a value+ccy pair by the
  // same helper the renderers use; placed before the raw-null guard because the
  // displayed figure may come from the `*_converted` twin when the native field
  // is absent.
  switch (colId) {
    case 'margin_price': {
      const d = getSettlementDisplay(item, 'margin_price');
      return d === null ? '' : d.value.toFixed(2);
    }
    case 'contract_value':
    case 'daily_earnings': {
      const d = getSettlementDisplay(item, colId === 'contract_value' ? 'contract_value' : 'daily_earnings');
      // QA finding D: a coerced/absent numeric can be NaN — emit '' not "NaN".
      return d === null || !Number.isFinite(d.value)
        ? ''
        : d.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    case 'closing_cash': {
      if (!showsClosingCash(item)) return '';
      const d = getSettlementDisplay(item, 'closing_cash');
      return d === null
        ? ''
        : d.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    }
    // LR-522: Last Marked Margin Price copies the displayed settlement price.
    case 'marked_margin_price': {
      const d = getSettlementDisplay(item, 'marked_margin_price');
      return d === null ? '' : d.value.toFixed(2);
    }
    // LR-522: Trade Gross Value + Last Marked gross/margin VALUES copy the
    // displayed settlement figure (blank when the twin is null — e.g. never marked).
    case 'trade_gross_value':
    case 'last_marked_gross_value':
    case 'marked_contract_value': {
      const d = getSettlementDisplay(item, colId as SettlementMoneyField);
      return d === null || !Number.isFinite(d.value)
        ? ''
        : d.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // LR-494: copy the COB FX Rate as the displayed inverted pair value
    // (1 / stored rate, COB→booked fallback) via the shared helper — numbers
    // only, no pair label (the money-cell convention). One source of truth with
    // the cell / CSV; same-ccy / no-rate → '' (the cell shows a dash).
    case 'cob_fx_rate': {
      const display = getDisplayedCobFxRate(item);
      return display != null ? formatDecimalString(display.value, { dp: 6 }) : '';
    }
    // LR-522: Trade FX Rate — displayed inverted booked rate (1 / stored),
    // numbers only, blank for same-ccy (matches the cell / CSV).
    case 'fx_rate': {
      const v = getDisplayedBookedFxRate(item);
      return v != null ? formatDecimalString(v, { dp: 6 }) : '';
    }
    case 'fee_income':
    case 'cost_of_collateral':
    case 'net_amount': {
      const v = RATE_LEG_CELLS[colId](item);
      return v == null || !Number.isFinite(v)
        ? ''
        : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    }
  }

  const value = (item as unknown as Record<string, unknown>)[colId];

  if (value === null || value === undefined) {
    // Maturity date renders as "Open" when null
    if (colId === 'maturity_date') return 'Open';
    return '';
  }

  switch (colId) {
    case 'quantity':
      return typeof value === 'number' ? value.toLocaleString('en-US') : String(value);

    // LR-522: Last Marked Gross Price renders native toFixed(2) like Trade Gross
    // Price (both security-ccy raw prices) — copy must match the cell.
    case 'gross_price':
    case 'latest_mark_price':
      return typeof value === 'number' ? value.toFixed(2) : String(value);

    case 'margin':
      return typeof value === 'number' ? `${value.toFixed(2)}%` : String(value);

    case 'trade_date':
    case 'maturity_date':
    case 'settlement_date':
    case 'actual_settlement_date': {
      // LR-406: Open Settlement Date and Close Settlement Date are now
      // separate columns — each formats its own raw value, no override
      // (previously `settlement_date` substituted actual_settlement_date,
      // which mixed clipboard semantics with the visible cell).
      // Same formatter as the cell (formatDate renders the literal Y-M-D in UTC),
      // so the clipboard matches the screen; unparseable → '' (the cell shows a dash).
      const formatted = formatDate(String(value));
      return formatted === '—' ? '' : formatted;
    }

    case 'created_at':
    case 'updated_at':
      // Canonical UTC timestamp, matching the cell.
      return formatDateTimeUtc(String(value));

    case 'term_type_display': {
      if (value) return String(value);
      const termType = item.term_type;
      if (termType === 'evergreen') return 'Evergreen';
      if (termType === 'open') return 'Open';
      return 'Term';
    }

    default:
      return String(value);
  }
}
